import React, { useEffect, useRef, useState } from 'react';
import EmployeeInfo from './EmployeeInfo';
import ContactPanel from './ContactPanel';
import { fetchWithCorpId } from '../utils/api';
import { CONTACT_OWNERS, listContacts, saveContacts } from '../utils/contacts';
import { useParams } from 'react-router-dom';
import { FormLoading } from './ui/formkit';
import StepFlow, { FlowNote, TabAttentionDot } from './ui/StepFlow';
import { useToast } from './ui/Toast';
import { toDateInputValue } from '../utils/dateUtils';

const DEFAULT_EMPLOYEE = {
  firstName: '',
  middleName: '',
  lastName: '',
  designation: '',
  role: '',
  status: 'Active',
  startDate: toDateInputValue(new Date().toISOString()),
  endDate: '',
  email: '',
  phone: '',
  guestCode: '',
  workAuth: '',
  category: '',
  spouseName: '',
  childrenCount: '',
  emergencyContact: '',
};

// Create-mode stepper: names shown in the step header.
const CREATE_STEPS = ['Details', 'Contacts', 'Family (optional)'];

// Fields the Details step requires; mirrors the `required` marks in
// EmployeeInfo and what the API needs to create the record.
const REQUIRED_DETAILS = [
  ['firstName', 'First name'],
  ['lastName', 'Last name'],
  ['designation', 'Designation'],
  ['category', 'Category'],
  ['workAuth', 'Work auth'],
];

const EMAIL_RULE = 'An email contact is required. The employee signs in with it.';
const EMAIL_RE = /^\S+@\S+\.\S+$/;


function EmployeeForm({
  mode = 'create',
  initialData = {},
  employeeId,                 // optional id; overrides URL param
  formRef,                    // optional ref attached to <form>
  hideActions = false,        // hide inline Save/Cancel (edit mode only; the create stepper always owns its footer)
  onSuccess,
  onCancel,
}) {
  const params = useParams();
  const toast = useToast();
  const id = employeeId ?? params.id;
  const isEdit = mode === 'edit';
  const [activeTab, setActiveTab] = useState('info');   // edit mode tabs
  const [step, setStep] = useState(1);                  // create mode stepper (1..3)
  const [contacts, setContacts] = useState([]);
  const loadedContactsRef = useRef([]);
  const [employee, setEmployee] = useState(DEFAULT_EMPLOYEE);
  const [message, setMessage] = useState('');
  const [loadingRecord, setLoadingRecord] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  // Snapshot of the last saved state; drives the drawer's discard guard.
  // In edit mode it is set once the record loads.
  const initialSnapshotRef = useRef(isEdit ? null : JSON.stringify({ ...DEFAULT_EMPLOYEE, contacts: [] }));

  useEffect(() => {
    const fetchEmployee = async () => {
      if (isEdit && id) {
        setLoadingRecord(true);
        try {
          const data = await fetchWithCorpId(`/employees/${id}`);
          const normalized = {
            ...data,
            startDate: toDateInputValue(data.startDate),
            endDate: toDateInputValue(data.endDate),
          };
          // Seed contacts from the loaded record. Without this the drawer's edit
          // mode kept contacts as [] and submitting wiped the employee's contacts
          // (the entity uses orphanRemoval).
          const loadedContacts = await listContacts(CONTACT_OWNERS.EMPLOYEE, id);
          loadedContactsRef.current = loadedContacts;
          setEmployee(normalized);
          setContacts(loadedContacts);
          initialSnapshotRef.current = JSON.stringify({ ...normalized, contacts: loadedContacts });
        } catch (error) {
          setMessage('Could not load this employee: ' + error.message);
        } finally {
          setLoadingRecord(false);
        }
      } else {
        setLoadingRecord(false);
      }
    };
    fetchEmployee();
  }, [isEdit, id]);

  // Initialize form data when initialData changes
  useEffect(() => {
    if (!initialData || Object.keys(initialData).length === 0) return;
    // Ensure all fields are properly initialized
    const normalized = {
      ...DEFAULT_EMPLOYEE,
      ...initialData,
      // Ensure dates are in the correct format
      startDate: initialData.startDate ? toDateInputValue(initialData.startDate) : DEFAULT_EMPLOYEE.startDate,
      endDate: initialData.endDate ? toDateInputValue(initialData.endDate) : '',
      // Ensure status has a default value
      status: initialData.status || 'Active',
      role: initialData.role || '',
      // Ensure other fields have default values if not provided
      firstName: initialData.firstName || '',
      middleName: initialData.middleName || '',
      lastName: initialData.lastName || '',
      designation: initialData.designation || '',
      email: initialData.email || '',
      phone: initialData.phone || '',
      guestCode: initialData.guestCode || '',
      workAuth: initialData.workAuth || '',
      category: initialData.category || '',
      spouseName: initialData.spouseName || '',
      childrenCount: initialData.childrenCount || '',
      emergencyContact: initialData.emergencyContact || '',
    };
    const nextContacts = Array.isArray(initialData.contacts) ? initialData.contacts : [];
    loadedContactsRef.current = nextContacts;
    setEmployee(normalized);
    setContacts(nextContacts);
    initialSnapshotRef.current = JSON.stringify({ ...normalized, contacts: nextContacts });
    setLoadingRecord(false);
  }, [initialData]);

  const isDirty = initialSnapshotRef.current !== null
    && JSON.stringify({ ...employee, contacts }) !== initialSnapshotRef.current;

  const hasEmailContact = (contacts || []).some(
    (c) => c.email1 && c.email1.trim()
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmployee(prev => ({ ...prev, [name]: value }));
  };

  const handleResendLogin = async () => {
    try {
      const res = await fetchWithCorpId(`/employees/${employee.id || id}/resend-login`, { method: 'POST' });
      toast.success(res.message || 'Invite sent by email.');
    } catch (err) {
      setMessage(err.message || 'Could not resend the invite.');
    }
  };

  // Advance the create stepper; validates the current step and blocks with an
  // inline error on failure.
  const advanceStep = () => {
    if (step === 1) {
      const missing = REQUIRED_DETAILS
        .filter(([key]) => !(employee[key] || '').trim())
        .map(([, label]) => label);
      if (missing.length > 0) {
        setMessage('Fill in the required fields: ' + missing.join(', ') + '.');
        return;
      }
    } else if (step === 2) {
      if (!hasEmailContact) {
        setMessage(EMAIL_RULE);
        return;
      }
      const badEmail = contacts.some(
        (c) => c.email1 && c.email1.trim() && !EMAIL_RE.test(c.email1.trim())
      );
      if (badEmail) {
        setMessage('Enter a valid email address for the email contact.');
        return;
      }
    }
    setMessage('');
    setStep(s => Math.min(s + 1, CREATE_STEPS.length));
  };

  const goToStep = (n) => {
    setMessage('');
    setStep(n);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // In create mode, Enter and the footer button advance the current step;
    // the record is only submitted from the last step.
    if (!isEdit && step < CREATE_STEPS.length) {
      advanceStep();
      return;
    }
    if (saving) return;
    setMessage('');

    // login details and password resets are delivered by email, so one is required
    if (!hasEmailContact) {
      setMessage(EMAIL_RULE);
      if (isEdit) {
        setActiveTab('contact');
      } else {
        setStep(2);
      }
      return;
    }

    setSaving(true);
    try {
      const url = isEdit
        ? `/employees/${employee.id}`
        : '/employees';

      const res = await fetchWithCorpId(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(employee),
      });

      if (res) {
        const savedContacts = await saveContacts(
          CONTACT_OWNERS.EMPLOYEE, res?.id || employee.id, contacts, loadedContactsRef.current);
        loadedContactsRef.current = savedContacts;
        setContacts(savedContacts);
        initialSnapshotRef.current = JSON.stringify({ ...employee, contacts: savedContacts });
        toast.success(res.accountMessage
          ? 'Employee saved. Invite sent by email.'
          : (isEdit ? 'Employee updated' : 'Employee created'));
        onSuccess?.();
      } else {
        setMessage(`Failed to ${isEdit ? 'update' : 'create'} employee. Please try again.`);
      }
    } catch (err) {
      setMessage(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const tabButtonStyle = (tab) => ({
    padding: '10px 18px',
    cursor: 'pointer',
    border: 'none',
    borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
    background: activeTab === tab ? 'var(--surface-card)' : 'transparent',
    color: activeTab === tab ? 'var(--ink)' : 'var(--muted)',
    fontFamily: 'var(--font-sans)',
    fontSize: '14px',
    fontWeight: 500,
    letterSpacing: 0,
    transition: 'background 0.16s, color 0.16s',
  });

  if (loadingRecord) {
    return <FormLoading label="Loading employee…" />;
  }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      noValidate={!isEdit}
      data-dirty={isDirty ? 'true' : 'false'}
    >
      {message && (
        <div className="alert error" role="alert" style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      {isEdit && (
        <div style={{ marginBottom: '16px' }}>
          <button type="button" className="btn-secondary" onClick={handleResendLogin}>
            Resend invite
          </button>
          <span style={{
            marginLeft: '10px',
            fontFamily: 'var(--font-sans)', fontSize: '12px', color: 'var(--muted-soft)',
          }}>
            Emails the employee a new link to set their password.
          </span>
        </div>
      )}

      {isEdit ? (
        <>
          <div role="tablist" style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid var(--hairline)' }}>
            <button type="button" role="tab" aria-selected={activeTab === 'info'} style={tabButtonStyle('info')} onClick={() => setActiveTab('info')}>Info</button>
            <button type="button" role="tab" aria-selected={activeTab === 'contact'} style={tabButtonStyle('contact')} onClick={() => setActiveTab('contact')}>
              Contacts
              {!hasEmailContact && <TabAttentionDot label="required" />}
            </button>
          </div>

          {activeTab === 'info' && (
            <EmployeeInfo employee={employee} handleChange={handleChange} />
          )}

          {activeTab === 'contact' && (
            <>
              <FlowNote>{EMAIL_RULE}</FlowNote>
              <ContactPanel contacts={contacts} setContacts={setContacts} />
            </>
          )}


          {!hideActions && (
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              {onCancel && (
                <button type="button" onClick={onCancel} className="btn-secondary" disabled={saving}>Cancel</button>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <StepFlow steps={CREATE_STEPS} current={step} onStepClick={goToStep} />

          {step === 1 && (
            <EmployeeInfo employee={employee} handleChange={handleChange} />
          )}

          {step === 2 && (
            <>
              <FlowNote>{EMAIL_RULE}</FlowNote>
              <ContactPanel contacts={contacts} setContacts={setContacts} />
            </>
          )}


          {/* The stepper owns its footer in both page and drawer mode. */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            {step === 1 && !hideActions && onCancel && (
              <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
            )}
            {step > 1 && (
              <button type="button" className="btn-secondary" onClick={() => goToStep(step - 1)} disabled={saving}>
                Back
              </button>
            )}
            <button type="submit" className="btn-primary" disabled={saving}>
              {step === 1 && 'Next: Contacts'}
              {step === 2 && 'Next: Family'}
              {step === 3 && (saving ? 'Saving…' : 'Create employee')}
            </button>
          </div>
        </>
      )}
    </form>
  );
}

export default EmployeeForm;
